export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message)
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? `HTTP ${res.status}`, data?.detail)
  }
  return data as T
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T,>(path: string) => request<T>('DELETE', path),
}

/* ------------------------------------------------------------------ */
/* 共用型別                                                            */
/* ------------------------------------------------------------------ */

export interface Writer {
  id: number
  status: string
  /** 個人（實際支付）/ 廠商（計 5% 稅）/ 內部發包（實際支付，並帶出「內包」） */
  kind: string
  /** 內部發包才有：對應到 staff 的哪一位。驗收單據此排除「承包人本人」 */
  staff_id: number | null
  lang: string
  name: string
  name_en: string | null
  signed_on: string | null
  expires_on: string | null
  default_price: number
  monthly_min: number | null
  monthly_max: number | null
  email: string | null
  review_folder: string | null
  archive_doc: string | null
  note: string | null
}

export interface Client { id: number; name: string; short_name: string | null }
export interface Staff { id: number; name: string; roles: string }
export interface JobType { id: number; name: string; lang: string; default_price: number }

export interface Lookup {
  writers: Writer[]
  clients: Client[]
  staff: Staff[]
  job_types: JobType[]
}

export interface Evaluation {
  id: number
  period: string
  checked: number
  dispatched: number
  dispatch_type: string
  owner_id: number | null
  owner_name: string | null
  client_id: number
  client_name: string
  contract_code: string | null
  case_link: string | null
  writer_id: number | null
  writer_name: string | null
  ask_writer_on: string | null
  due_on: string | null
  eip_due_on: string | null
  qty: number
  unit_price: number
  amount: number
  work_order_code: string | null
  status: string
  note: string | null
  /** 審核退回原因；核准後清空。狀態為「評估中」又有值＝被退回過 */
  review_note?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
}

export interface Memo {
  id: number
  writer_id: number
  writer_name?: string
  writer_name_en?: string | null
  writer_kind?: string
  /** 乙方全銜；留白時列印用寫手姓名 */
  party_b_title: string | null
  rep_name: string | null
  address: string | null
  phone: string | null
  /** 身分證字號／統一編號 */
  id_no: string | null
  effective_from: string | null
  effective_to: string | null
  signed_on: string | null
  /** 條款參數：範本寫死的數字，逐份可調 */
  revision_limit: number
  settle_day: number
  withhold_tax_pct: number
  supplement_pct: number
  /** 草稿 / 待審核 / 已生效 / 已終止 */
  status: string
  review_note?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  /** 八條條款正文；留白代表尚未設定 */
  terms: string | null
  note: string | null
}

export interface WorkOrderItem {
  id?: number
  seq: number
  client_id: number
  client_name?: string
  /** 合約編號：每個案件不同，由使用者直接輸入，不做主檔 */
  contract_code: string | null
  job_type_id: number | null
  job_type_name?: string
  owner_id: number | null
  owner_name?: string
  service: string | null
  qty: number
  unit_price: number
  subtotal: number
  accepted_qty?: number
  /**
   * 這一列原本屬於哪張發包單（NULL = 本來就是這張單的）。
   * 合併時寫入，取消合併時據此搬回去。儲存發包單時必須原樣送回，
   * 否則存過一次檔就失去還原能力。
   */
  origin_code?: string | null
}

export interface WorkOrderArticle {
  id?: number
  seq: number
  item_id: number | null
  client_id: number | null
  title: string | null
  keyword: string | null
  word_target: number | null
  due_on: string | null
  /** 同 WorkOrderItem.origin_code */
  origin_code?: string | null
}

export interface WorkOrder {
  id: number
  code: string
  period: string
  /** 外包 / 內包，由評估表帶入 */
  dispatch_type: string
  writer_id: number
  writer_name?: string
  writer_name_en?: string
  /** 明細裡出現過的客戶簡稱，逗號分隔（待驗收清單顯示用） */
  client_names?: string | null
  issuer_id: number | null
  issuer_name?: string
  /** 承包人聯絡方式（自寫手 Email 帶入，可逐單覆寫） */
  issuer_contact: string | null
  /** 寫手主檔上的 Email，供 issuer_contact 空白時遞補 */
  writer_email?: string
  owner_id: number | null
  owner_name?: string
  dept: string | null
  issued_on: string | null
  confirmed_on: string | null
  due_on: string | null
  lang: string | null
  tax_mode: string
  rate_level: string | null
  /** 注意事項；留白代表沿用該發包類型的預設條款 */
  terms: string | null
  deliver_link: string | null
  job_type_id: number | null
  job_type_name?: string
  total_qty: number
  total_amount: number
  status: string
  /** 審核退回原因；核准後清空。狀態為「待補充」又有值＝被退回過 */
  review_note?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  revise_count: number
  /** 有幾筆評估表是由此發包單轉入的（0 代表非評估表轉入，例如手動新增） */
  evaluation_count?: number
  /** 已有幾筆驗收紀錄；大於 0 就不可刪除／退回評估 */
  acceptance_count?: number
  /** 已驗收通過的篇數合計（明細加總）；小於 total_qty 代表還沒驗完 */
  accepted_qty?: number
  /** 有幾列明細是從別張單合併進來的；大於 0 代表可以取消合併 */
  merged_item_count?: number
  note: string | null
  items: WorkOrderItem[]
  articles: WorkOrderArticle[]
}

export interface CheckDefinition {
  code: string
  category: string
  label: string
  sort: number
}

export interface Acceptance {
  id: number
  code: string
  period: string
  work_order_id: number
  work_order_code?: string
  /** 來自發包單，用來決定列印抬頭的副標（外包／內包） */
  dispatch_type?: string
  /** 明細裡出現過的客戶名稱，逗號分隔（列表頁篩選用） */
  client_names?: string | null
  writer_name?: string
  reviewer_name?: string
  batch_no: number
  due_on: string | null
  delivered_on: string | null
  reviewed_on: string | null
  passed_qty: number
  amount: number
  conclusion: string
  remark: string | null
  /** 有值代表已作廢：紀錄留著，但所有連動都已反向沖銷 */
  voided_at?: string | null
  voided_by?: string | null
  void_reason?: string | null
  items: any[]
  checks: any[]
}

/** 作廢預覽：按下確認前先讓使用者看到實際會發生什麼 */
export interface VoidPreview {
  code: string
  work_order_code: string
  can_void: boolean
  blockers: string[]
  billings_to_delete: Array<{ id: number; seq: number; qty: number; amount: number; status: string }>
  accepted_qty_revert: Array<{ item_id: number; n: number }>
  status_from: string
  status_to: string
  revise_count_from: number
  revise_count_to: number
  snapshot_used: boolean
}

export interface Billing {
  id: number
  period: string
  client_name: string
  work_order_code: string
  acceptance_code: string | null
  contract_code: string | null
  writer_name: string
  job_type_name: string | null
  qty: number
  unit_price: number
  amount: number
  note: string | null
  status: string
}

export interface MappingConfig {
  id: number
  target: string
  label: string
  spreadsheet_id: string
  sheet_name: string
  header_row: number
  start_row: number
  max_rows: number | null
  write_mode: string
  enabled: number
  fields: Array<{ field: string; col_letter: string; sort: number }>
}
